import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import NavBar from "../../components/common/NavBar";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { confirmDelete } from "../../components/common/confirmDelete";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

// 🔎 Query campaigns owned by this user (via their Sponsor account(s))
const GET_MY_CAMPAIGNS = gql`
    query GetMyCampaigns($page: Int, $pageSize: Int) {
        campaigns(page: $page, pageSize: $pageSize) {
            objects {
                id
                name
                status
                startsAt
                endsAt
                sponsor {
                    name
                }
                zones {
                    key
                }
                targeting {
                    countries
                    platforms
                    sports
                }
            }
            page
            pages
            hasPrev
            hasNext
            totalCount
        }
    }
`;

// ❌ Delete campaign
const DELETE_CAMPAIGN = gql`
    mutation DeleteCampaign($id: ID!) {
        deleteAdCampaign(id: $id) {
            success
        }
    }
`;

function Campaigns() {
    const { t } = useTranslation();
    const scopeId = "kbola-campaigns-page";
    const navigate = useNavigate();

    const navBarElem = {
        pageTitle: t('campaigns.advertising'),
        links: [
            { pageTitle: t('campaigns.advertising'), navLink: null, active: false },
            { pageTitle: t('campaigns.campaigns'), navLink: `/advertise/campaign`, active: true },
        ],
    };

    const [page, setPage] = useState(1);
    const pageSize = 12;

    const [totalPages, setTotalPages] = useState(1);
    const [campaigns, setCampaigns] = useState([]);

    const { loading, error, refetch } = useQuery(GET_MY_CAMPAIGNS, {
        variables: { page, pageSize },
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            const payload = data?.campaigns;
            setCampaigns(payload?.objects || []);
            setTotalPages(payload?.pages || 1);
        },
    });

    const [deleteCampaign] = useMutation(DELETE_CAMPAIGN);

    const handleDelete = (campaign) => {
        confirmDelete({
            title: t('campaigns.deleteCampaign'),
            message: t('campaigns.deleteConfirmMessage', { name: campaign.name }),
            confirmLabel: t('campaigns.deleteConfirmLabel'),
            cancelLabel: t('campaigns.cancelLabel'),
            danger: true,
            onConfirm: async () => {
                try {
                    const { data } = await deleteCampaign({ variables: { id: campaign.id } });

                    if (data?.deleteAdCampaign?.success) {
                        setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
                        toast.success(t('campaigns.campaignDeleted'), {
                            autoClose: 2500,
                            toastId: "campaign_deleted",
                            position: "top-center",
                            closeOnClick: true,
                        });
                        refetch({ page, pageSize });
                    }
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, 'campaigns.failedToDelete'), {
                        autoClose: 3000,
                        toastId: "campaign_delete_err",
                        position: "top-center",
                        closeOnClick: true,
                    });
                }
            },
        });
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        const d = new Date(iso);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const da = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${da}`;
    };

    const formatTargetValue = (type, value) => {
        if (!value) return "";

        const fallback = String(value)
            .split(/[-_]/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");

        if (type === "sports") {
            return t(`signUp.form.sports.${value}`, {}, fallback);
        }

        return t(`campaigns.${type}.${value}`, {}, fallback);
    };

    const formatTargetList = (type, values, emptyLabel) => {
        return values?.length
            ? values.map((value) => formatTargetValue(type, value)).join(", ")
            : emptyLabel;
    };

    const campaignTargetSummary = (campaign) => {
        const targeting = campaign?.targeting || {};
        const zones = campaign?.zones || [];

        const geo = formatTargetList("countries", targeting.countries, t('campaigns.allCountries'));
        const platforms = formatTargetList("platforms", targeting.platforms, t('campaigns.allPlatforms'));
        const sports = formatTargetList("sports", targeting.sports, t('campaigns.allSports'));

        const zoneCount = zones.length;
        const zoneLabel =
            zoneCount === 0 ? t('campaigns.noZones') : zoneCount === 1 ? zones[0].key : t('campaigns.zonesCount', { count: zoneCount });

        return { geo, platforms, sports, zoneLabel };
    };

    return (
        <>
            <div className="content-wrapper" id={scopeId}>
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    {error && (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                        </div>
                    )}

                    <PaginatedFlatTable
                        title={t('campaigns.campaigns')}
                        page={page}
                        onAdd={() => navigate("new")}
                        pages={totalPages}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                        }}
                        items={campaigns}
                        loading={loading}
                        headers={[
                            t('campaigns.headers.name'),
                            t('campaigns.headers.status'),
                            t('campaigns.headers.sponsor'),
                            t('campaigns.headers.dates'),
                            t('campaigns.headers.targeting'),
                            t('campaigns.headers.actions')
                        ]}
                        renderRow={(campaign, index) => {
                            const summary = campaignTargetSummary(campaign);

                            return (
                                <tr key={campaign.id || index}>
                                    <td style={{ verticalAlign: "middle" }}>
                                        <div className="font-weight-bold">{campaign.name || "—"}</div>
                                        <div className="text-muted text-xs">{t('campaigns.zones')}: {summary.zoneLabel}</div>
                                    </td>

                                    <td style={{ verticalAlign: "middle" }}>
                    <span
                        className={`badge ${
                            campaign.status === "RUNNING"
                                ? "bg-success"
                                : campaign.status === "PAUSED"
                                    ? "bg-warning"
                                    : campaign.status === "ENDED"
                                        ? "bg-secondary"
                                        : "bg-info"
                        }`}
                        style={{ textTransform: "capitalize" }}
                    >
                      {campaign.status ? t(`campaigns.status.${campaign.status.toLowerCase()}`) : "—"}
                    </span>
                                    </td>

                                    <td style={{ verticalAlign: "middle" }}>{campaign.sponsor?.name || "—"}</td>

                                    <td style={{ verticalAlign: "middle" }}>
                                        <div className="text-nowrap">
                                            <strong>{t('campaigns.start')}:</strong> {formatDate(campaign.startsAt)}
                                        </div>
                                        <div className="text-nowrap">
                                            <strong>{t('campaigns.end')}:</strong> {formatDate(campaign.endsAt)}
                                        </div>
                                    </td>

                                    <td style={{ verticalAlign: "middle" }}>
                                        <div className="text-xs text-muted">
                                            <strong>{t('campaigns.geo')}:</strong> {summary.geo}
                                        </div>
                                        <div className="text-xs text-muted">
                                            <strong>{t('campaigns.platform')}:</strong> {summary.platforms}
                                        </div>
                                        <div className="text-xs text-muted">
                                            <strong>{t('campaigns.sport')}:</strong> {summary.sports}
                                        </div>
                                    </td>

                                    {/* Actions (NEW STYLE) */}
                                    <td className="text-right" style={{ verticalAlign: "middle" }}>
                                        <div style={{ display: "inline-flex", gap: 8 }}>
                                            <button
                                                type="button"
                                                className="kbola-action kbola-action-edit"
                                                onClick={() => {
                                                    navigate(`/advertise/campaign/${encodeURIComponent(campaign.id)}/edit`);
                                                }}
                                                title={t('campaigns.editCampaign')}
                                            >
                                                <i className="fas fa-edit" />
                                            </button>

                                            {campaign.status === "DRAFT" ? (
                                                <button
                                                    type="button"
                                                    className="kbola-action kbola-action-danger"
                                                    onClick={() => handleDelete(campaign)}
                                                    title={t('campaigns.deleteCampaign')}
                                                >
                                                    <i className="fas fa-trash-alt" />
                                                </button>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            );
                        }}
                    />
                </section>

                {/* ✅ Page-scoped styles (same as Venues action buttons) */}
                <style>{`
          #${scopeId}{
            font-family: 'Poppins', sans-serif;
          }

          #${scopeId} .kbola-action{
            width: 38px;
            height: 38px;
            border-radius: 12px;
            border: 1px solid rgba(0,0,0,0.10);
            background: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0;
          }

          #${scopeId} .kbola-action-edit{
            border: 1px solid rgba(0,0,0,0.10);
            background: rgba(0,0,0,0.02);
            color: #14151a;
          }

          #${scopeId} .kbola-action-danger{
            border: 1px solid rgba(220,53,69,0.35);
            background: rgba(220,53,69,0.06);
            color: #dc3545;
          }

          /* Focus ring */
          #${scopeId} button:focus,
          #${scopeId} .btn:focus,
          #${scopeId} .form-control:focus{
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
          }

          /* FontAwesome safety */
          #${scopeId} i.fas,
          #${scopeId} i.far,
          #${scopeId} i.fal,
          #${scopeId} i.fa,
          #${scopeId} i[class^="fa-"],
          #${scopeId} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }
          #${scopeId} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }
        `}</style>
            </div>
        </>
    );
}

export default Campaigns;
